import React, { useEffect, useMemo, useState } from "react";
import planPilotService from "../../services/planPilotService";
import SunriseIframeTest from "../Iframe";

const PlanPilotView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [departments, setDepartments] = useState([]);
  const [learners, setLearners] = useState([]);

  // UI: which panel is open
  const [activeView, setActiveView] = useState(null); // "DEPARTMENTS" | "LEARNERS" | null

  // Filters
  const [deptSearch, setDeptSearch] = useState("");
  const [learnerSearch, setLearnerSearch] = useState("");
  const [filterLehrjahr, setFilterLehrjahr] = useState("");
  const [filterBeruf, setFilterBeruf] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [deptData, learnerData] = await Promise.all([
          planPilotService.getDepartments(),
          planPilotService.getLearners(),
        ]);
        setDepartments(deptData || []);
        setLearners(learnerData || []);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch PlanPilot data", err);
        if (err?.response?.status === 403) {
          setError(
            "Access restricted: you do not have permission to view this data.",
          );
        } else if (err?.response?.status === 503) {
          setError("PlanPilot integration is currently disabled.");
        } else {
          setError("Failed to load data. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredDepartments = useMemo(() => {
    const q = deptSearch.toLowerCase().trim();
    if (!q) return departments;
    return departments.filter((d) =>
      Object.values(d).some((val) => String(val).toLowerCase().includes(q)),
    );
  }, [departments, deptSearch]);

  const filteredLearners = useMemo(() => {
    const q = learnerSearch.toLowerCase().trim();
    return learners.filter((l) => {
      const matchesSearch = !q || (l.name || "").toLowerCase().includes(q);

      const matchesLehrjahr = filterLehrjahr
        ? String(l.lehrjahr) === String(filterLehrjahr)
        : true;
      const matchesBeruf = filterBeruf ? l.beruf === filterBeruf : true;

      return matchesSearch && matchesLehrjahr && matchesBeruf;
    });
  }, [learners, learnerSearch, filterLehrjahr, filterBeruf]);

  const uniqueBerufe = useMemo(() => {
    return [
      ...new Set((learners || []).map((l) => l.beruf).filter(Boolean)),
    ].sort();
  }, [learners]);

  const clearLearnerFilters = () => {
    setLearnerSearch("");
    setFilterLehrjahr("");
    setFilterBeruf("");
  };

  const clearDeptFilters = () => {
    setDeptSearch("");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-green-cloud">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30 mb-6">
            <div className="h-7 w-56 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-96 bg-gray-200 rounded mt-3 animate-pulse" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="card-brand p-5">
                <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                <div className="h-8 w-20 bg-gray-200 rounded mt-3 animate-pulse" />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[0, 1].map((i) => (
              <div key={i} className="card-brand p-6">
                <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
                <div className="h-20 bg-gray-200 rounded mt-4 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center px-4">
        <div className="card-brand p-8 max-w-lg w-full text-center">
          <div className="w-16 h-16 bg-red-50 border border-red-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-circle text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-xl font-bold text-grey-dusk mb-2">PlanPilot</h2>
          <p className="text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 inline-flex items-center">
            <i className="fas fa-triangle-exclamation mr-2"></i>
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <img
                    src="/Planpilot_icon.png"
                    alt="PlanPilot Icon"
                    className="w-10 h-10 mr-2"
                  />
                  PlanPilot
                </h1>
                <p className="text-slate-cloud">
                  Departments and learners overview from the integration.
                </p>
              </div>

              <span className="px-4 py-2 bg-white rounded-full border border-sunrise-red/20 text-sunrise-red font-semibold text-sm flex items-center">
                <i className="fas fa-plug mr-2"></i>
                Integration
              </span>
            </div>
          </div>
        </div>

        {/* PlanPilot External Link Banner (corporate + clean) */}
        <a
          href="https://planpilot.sunrise-avengers.ch"
          target="_blank"
          rel="noopener noreferrer"
          className="block mb-6 group"
        >
          <div className="card-brand-elevated p-5 md:p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30 border border-pale-dawn hover:border-sunrise-red/30 transition-all hover:shadow-brand-lg">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Left */}
              <div className="flex items-start gap-4 min-w-0">
                <div className="w-12 h-12 rounded-2xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-arrow-up-right-from-square text-lg"></i>
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg md:text-xl font-bold text-grey-dusk leading-tight">
                      Open PlanPilot
                    </h2>
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white text-grey-dusk border border-pale-dawn">
                      External
                    </span>
                  </div>

                  <p className="text-sm text-slate-cloud mt-1">
                    Open the full PlanPilot dashboard to manage planning and
                    assignments.
                  </p>

                  <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                    <i className="fas fa-link text-sunrise-red"></i>
                    <span className="truncate">
                      planpilot.sunrise-avengers.ch
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </a>

        {/* Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <button
            type="button"
            onClick={() =>
              setActiveView((v) => (v === "DEPARTMENTS" ? null : "DEPARTMENTS"))
            }
            className={`text-left card-brand p-6 transition-all hover:shadow-brand-lg border-2 ${
              activeView === "DEPARTMENTS"
                ? "border-sunrise-red bg-white"
                : "border-transparent bg-white"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center">
                  <i className="fas fa-building text-lg"></i>
                </div>
                <div>
                  <div className="text-lg font-bold text-grey-dusk">
                    Departments
                  </div>
                  <div className="text-sm text-slate-cloud">
                    Capacity, manager and location
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-ice-white text-grey-dusk border border-pale-dawn">
                  {filteredDepartments.length} / {departments.length}
                </span>
                <i
                  className={`fas fa-chevron-right text-slate-cloud transition-transform ${
                    activeView === "DEPARTMENTS" ? "rotate-90" : ""
                  }`}
                ></i>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() =>
              setActiveView((v) => (v === "LEARNERS" ? null : "LEARNERS"))
            }
            className={`text-left card-brand p-6 transition-all hover:shadow-brand-lg border-2 ${
              activeView === "LEARNERS"
                ? "border-sunrise-red bg-white"
                : "border-transparent bg-white"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-green-cloud/10 text-green-cloud flex items-center justify-center">
                  <i className="fas fa-user-graduate text-lg"></i>
                </div>
                <div>
                  <div className="text-lg font-bold text-grey-dusk">
                    Apprentices
                  </div>
                  <div className="text-sm text-slate-cloud">
                    Filters by year and profession
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-ice-white text-grey-dusk border border-pale-dawn">
                  {filteredLearners.length} / {learners.length}
                </span>
                <i
                  className={`fas fa-chevron-right text-slate-cloud transition-transform ${
                    activeView === "LEARNERS" ? "rotate-90" : ""
                  }`}
                ></i>
              </div>
            </div>
          </button>
        </div>

        {/* Opened Panels */}
        {activeView === "DEPARTMENTS" && (
          <div className="card-brand overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-pale-dawn bg-gradient-to-r from-ice-white to-pale-dawn/30">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-grey-dusk flex items-center">
                    <i className="fas fa-building text-sunrise-red mr-3"></i>
                    Departments
                  </h2>
                  <p className="text-sm text-slate-cloud mt-1">
                    Search departments and inspect capacity.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <div className="relative w-full md:w-64">
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="text"
                      placeholder="Search..."
                      className="input-brand w-full pl-9"
                      value={deptSearch}
                      onChange={(e) => setDeptSearch(e.target.value)}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={clearDeptFilters}
                    className="px-4 py-2 bg-white border border-sunrise-red/20 text-sunrise-red rounded-lg font-medium hover:bg-sunrise-red hover:text-white transition-colors flex items-center gap-2"
                    title="Clear search"
                  >
                    <i className="fas fa-eraser"></i>
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop table (no horizontal scroll) */}
            <div className="hidden lg:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-ice-white text-gray-600 border-b border-pale-dawn">
                  <tr>
                    <th className="px-6 py-3 w-10"></th>
                    <th className="px-6 py-3">Name</th>
                    <th className="px-6 py-3">Manager</th>
                    <th className="px-6 py-3 text-right">Capacity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-pale-dawn">
                  {filteredDepartments.map((dept, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-ice-white/60 transition-colors"
                    >
                      <td className="px-6 py-3">
                        <div
                          className="w-3 h-3 rounded-full border border-gray-200"
                          style={{ backgroundColor: dept.color || "#ccc" }}
                          title={dept.color || "n/a"}
                        />
                      </td>
                      <td className="px-6 py-3 font-semibold text-grey-dusk">
                        {dept.name}
                      </td>
                      <td className="px-6 py-3 text-gray-700">
                        {dept.manager}
                      </td>
                      <td className="px-6 py-3 text-right font-semibold text-grey-dusk">
                        {dept.capacity}
                      </td>
                    </tr>
                  ))}
                  {filteredDepartments.length === 0 && (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-6 py-10 text-center text-gray-500"
                      >
                        No departments found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="lg:hidden p-4 space-y-3">
              {filteredDepartments.map((dept, idx) => (
                <div
                  key={idx}
                  className="border border-pale-dawn rounded-xl bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full border border-gray-200 mt-1"
                          style={{ backgroundColor: dept.color || "#ccc" }}
                        />
                        <div className="font-semibold text-grey-dusk truncate">
                          {dept.name}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        <i className="fas fa-user-tie mr-2 text-sunrise-red"></i>
                        {dept.manager}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        <i className="fas fa-location-dot mr-2 text-sunrise-red"></i>
                        {dept.location}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-xs text-gray-500">Capacity</div>
                      <div className="text-lg font-bold text-grey-dusk">
                        {dept.capacity}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {filteredDepartments.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <div className="w-14 h-14 bg-gray-50 border border-pale-dawn rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-folder-open text-gray-400 text-xl"></i>
                  </div>
                  No departments found.
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-pale-dawn bg-ice-white/40 text-xs text-gray-500 flex items-center justify-between">
              <span className="flex items-center">
                <i className="fas fa-list mr-2 text-sunrise-red"></i>
                Showing {filteredDepartments.length} of {departments.length}
              </span>
              <button
                type="button"
                onClick={() => setActiveView(null)}
                className="text-sunrise-red font-semibold hover:underline"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {activeView === "LEARNERS" && (
          <div className="card-brand overflow-hidden">
            <div className="px-6 py-4 border-b border-pale-dawn bg-gradient-to-r from-ice-white to-pale-dawn/30">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-bold text-grey-dusk flex items-center">
                      <i className="fas fa-user-graduate text-sunrise-red mr-3"></i>
                      Apprentices
                    </h2>
                    <p className="text-sm text-slate-cloud mt-1">
                      Filter by year and profession.
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={clearLearnerFilters}
                      className="px-4 py-2 bg-white border border-sunrise-red/20 text-sunrise-red rounded-lg font-medium hover:bg-sunrise-red hover:text-white transition-colors flex items-center gap-2"
                      title="Clear filters"
                    >
                      <i className="fas fa-eraser"></i>
                      Clear
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveView(null)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
                      title="Close"
                    >
                      <i className="fas fa-xmark"></i>
                      Close
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <div className="relative flex-1 min-w-[220px]">
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="text"
                      placeholder="Search name ..."
                      className="input-brand w-full pl-9"
                      value={learnerSearch}
                      onChange={(e) => setLearnerSearch(e.target.value)}
                    />
                  </div>

                  <select
                    className="input-brand min-w-[160px]"
                    value={filterLehrjahr}
                    onChange={(e) => setFilterLehrjahr(e.target.value)}
                  >
                    <option value="">All years</option>
                    <option value="1">1. Lehrjahr</option>
                    <option value="2">2. Lehrjahr</option>
                    <option value="3">3. Lehrjahr</option>
                    <option value="4">4. Lehrjahr</option>
                  </select>

                  <select
                    className="input-brand min-w-[180px]"
                    value={filterBeruf}
                    onChange={(e) => setFilterBeruf(e.target.value)}
                  >
                    <option value="">All professions</option>
                    {uniqueBerufe.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Desktop table (no horizontal scroll) */}
            <div className="hidden lg:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-ice-white text-gray-600 border-b border-pale-dawn">
                  <tr>
                    <th className="px-6 py-3">Name</th>
                    <th className="px-6 py-3 w-28">Year</th>
                    <th className="px-6 py-3 w-48">Profession</th>
                    <th className="px-6 py-3 w-56">Timeline</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-pale-dawn">
                  {filteredLearners.map((learner, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-ice-white/60 transition-colors"
                    >
                      <td className="px-6 py-3 font-semibold text-grey-dusk">
                        {learner.name}
                      </td>
                      <td className="px-6 py-3">
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white text-gray-700 border border-pale-dawn">
                          {learner.lehrjahr || "-"}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-gray-700">
                        {learner.beruf}
                      </td>
                      <td className="px-6 py-3 text-xs text-gray-500">
                        {learner.startDate || "—"}{" "}
                        <span className="text-gray-300 mx-1">–</span>{" "}
                        {learner.endDate || "—"}
                      </td>
                    </tr>
                  ))}
                  {filteredLearners.length === 0 && (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-6 py-10 text-center text-gray-500"
                      >
                        No learners found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="lg:hidden p-4 space-y-3">
              {filteredLearners.map((learner, idx) => (
                <div
                  key={idx}
                  className="border border-pale-dawn rounded-xl bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-grey-dusk truncate">
                        {learner.name}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        <i className="fas fa-briefcase mr-2 text-sunrise-red"></i>
                        {learner.beruf}
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        <i className="fas fa-timeline mr-2 text-sunrise-red"></i>
                        {learner.startDate || "—"}{" "}
                        <span className="text-gray-300 mx-1">–</span>{" "}
                        {learner.endDate || "—"}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-xs text-gray-500">Year</div>
                      <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-white text-gray-700 border border-pale-dawn">
                        {learner.lehrjahr || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {filteredLearners.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <div className="w-14 h-14 bg-gray-50 border border-pale-dawn rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-user-slash text-gray-400 text-xl"></i>
                  </div>
                  No learners found.
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-pale-dawn bg-ice-white/40 text-xs text-gray-500 flex items-center justify-between">
              <span className="flex items-center">
                <i className="fas fa-filter mr-2 text-sunrise-red"></i>
                Showing {filteredLearners.length} of {learners.length}
              </span>
              <span className="text-gray-500 flex items-center">
                <i className="fas fa-circle-info mr-2 text-sunrise-red"></i>
                {learnerSearch || filterLehrjahr || filterBeruf
                  ? "Filters active"
                  : "No filters"}
              </span>
            </div>
          </div>
        )}
        <SunriseIframeTest />
      </div>
    </div>
  );
};

export default PlanPilotView;
